import { useEffect, useState, useCallback, useRef } from "react";
import { getAllLinkedPaginatedServices } from "../services/linkedService";

interface IuseFetchLinkedServices {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  pSize: number;
  searchFilterData?: {
    query_name?: string;
    status_filter?: string;
  };
  sortBy?: string;
  sortOrder?: string;
}

const useFetchPaginatedAllLinkedServices = ({
  setIsLoading,
  page,
  pSize,
  searchFilterData,
  sortBy = "name",
  sortOrder = "asc",
}: IuseFetchLinkedServices) => {
  const [linkedServicesData, setLinkedServicesData] = useState<any>({
    items: [],
    total: 0,
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Cancel previous in-flight request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    try {
      // Extract filters from searchFilterData
      const searchTerm = searchFilterData?.query_name || "";
      const statusFilter = searchFilterData?.status_filter || "Active";

      // Convert status filter to isActive parameter
      const isActive =
        statusFilter === "All"
          ? undefined
          : statusFilter === "Active"
          ? "true"
          : "false";

      const result = await getAllLinkedPaginatedServices(
        page,
        pSize,
        searchTerm,
        controller.signal,
        sortBy,
        sortOrder,
        isActive
      );

      setLinkedServicesData(result);
    } catch (err: any) {
      // Ignore AbortError / canceled requests, reset on real errors
      if (err.name !== "AbortError" && err.code !== "ERR_CANCELED") {
        setLinkedServicesData({ items: [], total: 0 });
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    pSize,
    searchFilterData?.query_name,
    searchFilterData?.status_filter,
    setIsLoading,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchData();

    // Abort active request if the component unmounts or dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  return [linkedServicesData, fetchData] as const;
};

export default useFetchPaginatedAllLinkedServices;